import { MessageCircle, Search, UserCircle } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppLogin } from '../../hooks/useAppLogin';
import { hidesAgentBubble } from '../Agent/agentBubbleRoutes';
import { useAgentPanel } from '../Shell/AgentPanelContext';
import PathControl from '../Shell/PathControl';
import { useShell } from '../Shell/ShellContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const SITE_LINKS = [
  { name: 'About Saarim', url: 'https://saarim.me' },
  { name: 'Contact', url: 'mailto:saarim@berkeley.edu' },
  { name: 'Support Me', url: 'https://ko-fi.com/saarim' },
];

const SHORTCUT_LABEL = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K';

function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const login = useAppLogin();
  const user = useSelector((state: any) => state.user);
  const { openTools, openFeedback } = useShell();
  const { open: agentOpen, setOpen: setAgentOpen } = useAgentPanel();
  const signedIn = Boolean(user?.email);

  return (
    <TooltipProvider delayDuration={300}>
      <header className="sticky top-0 z-50 grow-0 border-b border-border bg-black/55 shadow-none backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-screen-xl items-center gap-2 px-3 sm:px-6">
          <PathControl />

          <div className="flex flex-1 justify-center">
            <button
              type="button"
              onClick={openTools}
              className="hidden h-9 w-full max-w-[340px] items-center gap-2 rounded-lg bg-white/[0.05] px-3 text-sm text-white/60 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] transition-colors hover:bg-white/[0.08] hover:text-white/80 md:flex"
            >
              <Search aria-hidden className="size-4" />
              Search tools
              <kbd className="ml-auto font-mono text-xs">{SHORTCUT_LABEL}</kbd>
            </button>
          </div>

          <div className="flex shrink-0 items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Search tools"
              onClick={openTools}
              className="text-white/90 hover:bg-white/5 md:hidden"
            >
              <Search className="size-5" />
            </Button>

            {hidesAgentBubble(pathname) ? null : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={agentOpen ? 'Close agent' : 'Open agent'}
                    aria-expanded={agentOpen}
                    aria-controls="agent-panel"
                    onClick={() => setAgentOpen((current) => !current)}
                    className={agentOpen ? 'bg-white/10 text-white' : 'text-white/90 hover:bg-white/5'}
                  >
                    <MessageCircle className="size-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{agentOpen ? 'Close SaarimGPT' : 'Ask SaarimGPT'}</TooltipContent>
              </Tooltip>
            )}

            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Account and site menu"
                      className="text-white/90 hover:bg-white/5"
                    >
                      <UserCircle className="size-5" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>Account and site menu</TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="end" className="w-52">
                {signedIn ? (
                  <DropdownMenuItem onClick={() => navigate('/profile')}>Profile</DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => login()}>Log In</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {SITE_LINKS.map((link) => (
                  <DropdownMenuItem key={link.name} asChild>
                    <a
                      href={link.url}
                      target={link.url.startsWith('http') ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      className="text-inherit no-underline"
                    >
                      {link.name}
                    </a>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={openFeedback}>Report an Issue</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
    </TooltipProvider>
  );
}

export default Header;
